import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { mockUser } from '../mockData';
import { User, AuthContextType } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, _password: string): Promise<{ success: boolean; error?: string }> => {
    // Mock login - replace with actual API call later
    setIsLoading(true);
    try {
      // Simulating API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const loggedInUser: User = { ...mockUser, email };
      setUser(loggedInUser);
      localStorage.setItem('user', JSON.stringify(loggedInUser));
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Invalid credentials' };
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (name: string, email: string, _password: string): Promise<{ success: boolean; error?: string }> => {
    // Mock signup - replace with actual API call later
    setIsLoading(true);
    try {
      // Simulating API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newUser: User = { ...mockUser, name, email, savedProperties: [] };
      setUser(newUser);
      localStorage.setItem('user', JSON.stringify(newUser));
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Signup failed' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  const toggleSaveProperty = (propertyId: string) => {
    if (!user) return;
    
    const savedProperties = user.savedProperties || [];
    const isSaved = savedProperties.includes(propertyId);
    
    const updatedUser: User = {
      ...user,
      savedProperties: isSaved
        ? savedProperties.filter(id => id !== propertyId)
        : [...savedProperties, propertyId]
    };
    
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const isPropertySaved = (propertyId: string): boolean => {
    if (!user) return false;
    return user.savedProperties?.includes(propertyId) || false;
  };

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    signup,
    logout,
    toggleSaveProperty,
    isPropertySaved
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
